'use client';

import { Box } from '@mui/material';
import { iframeResizer, type IFrameComponent } from 'iframe-resizer';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as v from 'valibot';
import { browserSupportsIframeSrcdoc, fetchIframeSrcDoc, getSrcValue, shouldLoadIframeSrcDoc, shouldRenderIframe } from './utils';
interface IFrameProps {
  scrolling: 'no';
  ref: (ref: IFrameComponent) => void;
  onLoad: () => void;
  src: string;
  srcDoc?: string;
}
export type BaseEmbedProps = {
  id?: string;
  src?: string;
  title: string;
  onReady?: () => void;
  deployableEnv: string;
};

// Alias for backward compatibility
export type Props = BaseEmbedProps;
export const BaseEmbed = ({
  id,
  src,
  title,
  onReady,
  deployableEnv
}: BaseEmbedProps) => {
  const [ready, setReady] = useState(false);
  const [iframeSrcDoc, setIframeSrcDoc] = useState('');
  const [srcDocFetchComplete, setSrcDocFetchComplete] = useState(false);
  const frameRef = useRef<IFrameComponent | null>(null);
  const visibleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const setIframeVisible = useCallback(() => {
    if (ready) return;
    if (visibleTimeoutRef.current) {
      clearTimeout(visibleTimeoutRef.current);
      visibleTimeoutRef.current = null;
    }
    setReady(true);
    onReady?.();
  }, [ready]);
  useEffect(() => {
    const onInfogramResize = (e: MessageEvent<string>) => {
      if (!frameRef.current) return false;
      if (frameRef.current.contentWindow !== e.source) return false;
      type MessageData = {
        height: string | number;
      };
      let data: MessageData;
      try {
        const schema = v.object({
          height: v.union([v.pipe(v.string(), v.trim(), v.minLength(1, 'height must have at least 1 character')), v.number()], 'height must be either a string or number')
        });
        data = v.parse(schema, e.data);
      } catch {
        return false;
      }
      frameRef.current.height = data.height.toString();
    };
    visibleTimeoutRef.current = setTimeout(setIframeVisible, 3000);
    window.addEventListener('message', onInfogramResize);
    return () => {
      window.removeEventListener('message', onInfogramResize);
      if (visibleTimeoutRef.current) clearTimeout(visibleTimeoutRef.current);
    };
  }, [setIframeVisible]);
  useEffect(() => {
    const fetchIframe = async () => {
      const compiledSrc = getSrcValue(id, src);
      if (shouldLoadIframeSrcDoc(compiledSrc)) {
        if (browserSupportsIframeSrcdoc) {
          try {
            const srcDoc = await fetchIframeSrcDoc(compiledSrc);
            setIframeSrcDoc(srcDoc);
            setSrcDocFetchComplete(true);
          } catch (err) {
            if (deployableEnv !== 'prod') {
              console.error('An error occurred in Embed: ', err);
            }
            setSrcDocFetchComplete(true);
          }
        } else {
          setSrcDocFetchComplete(true);
        }
      }
    };
    if (id || src) {
      void fetchIframe();
    }
    return () => {
      if (!frameRef.current) return;
      frameRef.current.iFrameResizer.close();
    };
  }, [id, src, deployableEnv]);
  const onIframeRef = (ref: IFrameComponent) => {
    if (!ref) return;
    frameRef.current = ref;
    const config: {
      checkOrigin: boolean;
      log?: boolean;
      warningTimeout?: number;
      onResized: () => void;
    } = {
      checkOrigin: false,
      log: false,
      warningTimeout: 0,
      onResized: () => {
        setIframeVisible();
      }
    };
    if (!frameRef.current.iFrameResizer) {
      iframeResizer(config, ref);
    }
  };
  const compiledSrc_0 = getSrcValue(id, src);
  if (!compiledSrc_0) return null;
  const iframeProps: IFrameProps = {
    scrolling: 'no',
    ref: onIframeRef,
    onLoad: () => {
      setIframeVisible();
    },
    src: compiledSrc_0
  };
  if (iframeSrcDoc) {
    iframeProps.srcDoc = iframeSrcDoc;
  }
  return <Box sx={{
    overflow: 'hidden'
  }}>
      {shouldRenderIframe(compiledSrc_0, srcDocFetchComplete) ? <iframe {...iframeProps} title={title} loading="lazy" style={{
      display: ready ? 'block' : 'none',
      border: 'none',
      width: '1px',
      minWidth: '100%'
    }} /> : null}
    </Box>;
};